import React, { useState, useRef, useEffect, useCallback } from "react";
import {Modal, Button, Row, Col, Checkbox, Skeleton, Input} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useActiveCompanyData } from "hooks/CompanyHooks";
import {
  contactSearch,
  getContactbyID,
} from "apiService/RESTApi";

const ContactSelector = ({
  isOpen = false,
  toggleModal = () => {},
  onOkCallback = (selectedContactData) => {},
  onCancelCallback = () => {},
  allowMultipleSelection = false,
  footerText = "Pressing OK will select the contacts you have checked.",
  buttonText = "OK",
  searchPageSize = 10,
}) => {

  const DEFAULT_INITIAL_TOTAL_PAGES = -1
  const SEARCH_DEBOUNCE_TIME = 200; // milliseconds
  const STALL_TIME = 250; // milliseconds

  const activeCompanyData = useActiveCompanyData();

  const [contactsFetched, setContactsFetched] = useState([]);
  const [searchIsLoading, setSearchIsLoading] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);

  const searchQueryInput = useRef('');
  const setSearchQueryInput = useCallback((value) => {
    searchQueryInput.current = value;
  }, []);
  const getSearchQueryInput = useCallback(() => {
    return searchQueryInput.current;
  }, [searchQueryInput]);

  const DEFAULT_PAGINATION_STATE = {
    currentPageNumber: 1,
    totalPageNumber: DEFAULT_INITIAL_TOTAL_PAGES,
  };
  const searchPaginationState = useRef(DEFAULT_PAGINATION_STATE);
  const setSearchPaginationState = useCallback((value) => {
    searchPaginationState.current = value;
  }, []);
  const getSearchPaginationState = useCallback(() => {
    return searchPaginationState.current;
  }, [searchPaginationState]);

  const totalElementCount = useRef(0);
  const setTotalElementCount = useCallback((value) => {
    totalElementCount.current = value;
  }, []);

  const hasMoreElementsToFetch = () => {
    return (
      (getSearchPaginationState().currentPageNumber < getSearchPaginationState().totalPageNumber) 
      || 
      (DEFAULT_INITIAL_TOTAL_PAGES === getSearchPaginationState().totalPageNumber)
    );
  }

  const resetContactListAndState = () => {
    setContactsFetched([]); // Triggers re-render
    setSelectedContacts([]); // Triggers re-render
    setSearchPaginationState(DEFAULT_PAGINATION_STATE);
    setTotalElementCount(0);
  }

  const onContactChecked = (event, contactData) => {
    const { checked } = event.target;
    setSelectedContacts((prevSelectedContacts) => {
      if (allowMultipleSelection) {
        if (checked) {
          return [...prevSelectedContacts, contactData.contactId];
        } else {
          return prevSelectedContacts.filter(
            (contactId) => contactId !== contactData.contactId
          );
        }
      } else {
        if (checked) {
          return [contactData.contactId];
        } else {
          return [];
        }
      }
    });
  }

  const onOk = () => {
    toggleModal()
    onOkCallback(selectedContacts);
    resetContactListAndState();
  };

  const onCancel = () => {
    toggleModal()
    handleInputChange(""); // Reset the search
    onCancelCallback();
  };

  const findContacts = useCallback(async (inputValue, pageNumber) => {
    let contactsFound = [];
    try {
      const customerArn = activeCompanyData.arn;
      if (!customerArn) {
        return contactsFound;
      }

      // Find contacts
      const searchResults = await contactSearch(
        customerArn,
        inputValue,
        pageNumber,
        searchPageSize
      );

      // Store response metadata
      setTotalElementCount(searchResults.page.totalElements);

      // Set pagination ref based on the server response
      setSearchPaginationState({
        currentPageNumber: searchResults.page.number + 1, // Convert to 1-based index
        totalPageNumber: searchResults.page.totalPages
      });

      // Get contact for each contactId
      const promiseArrayDefinition = searchResults?.data?.map((contact) => {
        return getContactbyID(contact?.contactId);
      });

      // Map the contact's data into what we need
      contactsFound = await Promise.all(promiseArrayDefinition).then((values) => {
        const contacts = values?.map((item) => ({
          contactId: item?.contactId,
          fullName: `${item?.firstName} ${item?.lastName}`,
        }));
        return contacts;
      });

    } catch (error) {
      console.error('An error occurred:', error);
    }
    return contactsFound;
  }, [activeCompanyData, searchPageSize, setTotalElementCount, setSearchPaginationState]);

  const listInnerRef = useRef();
  const isAtBottomOfScrollableArea = () => {
    if (listInnerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
      if (scrollTop + clientHeight >= scrollHeight * .96 || scrollHeight <= clientHeight) {
        return true;
      }
    }
    return false;
  };

  const onScroll = () => {
    if (!searchIsLoading && isAtBottomOfScrollableArea()) {
      search();
    }
  };

  const handleInputChange = (inputValue) => {
    resetContactListAndState();
    setSearchQueryInput(inputValue);
    search();
  };

  let debounceValue = "";
  const search = async () => {
    let inputValue = getSearchQueryInput();
    // console.log("debug inputValue: ", inputValue);
    if (inputValue) {
      inputValue = inputValue.trim();
    }

    if (inputValue.length === 0 || inputValue.length > 2) {
      clearTimeout(debounceValue);
      debounceValue = setTimeout(async () => {
        if (!hasMoreElementsToFetch()) {
          return;
        }

        setSearchIsLoading(true);
        const isInitialSearch = contactsFetched?.length === 0 ? true : false;

        let pageNumber = searchPaginationState.current.currentPageNumber;
        if (!isInitialSearch) {
          pageNumber = pageNumber + 1; // Increment page number (get next page)
        }

        const fetchContactsResponse = await findContacts(inputValue, pageNumber);

        // Merge the new contacts with the existing ones
        if (isInitialSearch) {
          setContactsFetched(fetchContactsResponse);
        }
        else {
          setContactsFetched((prevContacts) => {
            return [...prevContacts, ...fetchContactsResponse];
          });
        }

        setSearchIsLoading(false);

      }, SEARCH_DEBOUNCE_TIME);
    } else {
      clearTimeout(debounceValue);
    }
  }

  // OnMounted
  useEffect(() => {
    if(!activeCompanyData) {
      return;
    }
    
    const checkAndFillScrollArea = async () => {
      if (isOpen && !searchIsLoading && hasMoreElementsToFetch() && isAtBottomOfScrollableArea()) {
        await search();
        // Check again after render
        setTimeout(checkAndFillScrollArea, SEARCH_DEBOUNCE_TIME + STALL_TIME);
      }
    }

    search();
    if(isOpen) {
      checkAndFillScrollArea();
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompanyData, isOpen]);  


  return (
    <>
      {isOpen && (
        <Modal
          className="journey-test-modal"
          open={true}
          title={
            <span
              style={{
                fontSize: "18px",
                fontWeight: "600",
                lineHeight: "22px",
                color: "#252525",
              }}
            >
              Choose Contact
            </span>
          }
          onOk={onOk}
          onCancel={onCancel}
          footer={[
            <p key="0"
              style={{
                textAlign: "start",
                fontSize: "16px",
                fontWeight: "500",
                lineHeight: "19px",
                color: "#959595",
                marginBottom: "10px",
              }}
            >
              {footerText}
              <br/>
              Click <span style={{ color: "#252525" }}>{buttonText}</span> to confirm.
            </p>,
            <Button
              key="1"
              type={selectedContacts && selectedContacts.length > 0 ? "primary" : ""}
              className="standard-button primary-orange-button"
              style={{
                width: "100%",
              }}
              onClick={onOk}
              disabled={!selectedContacts || selectedContacts?.length <= 0}
            >
              {buttonText}
            </Button>,
          ]}
          centered
        >
          <Row gutter={[0, 16]} style={{ padding: "20px 0px" }}>
            <Input.Search
              type="search"
              className="standard-searchbox"
              allowClear
              prefix={<SearchOutlined className="standard-searchbox-prefix" />}
              placeholder="Search Contact Name"
              style={{ display: "block", height: "42px" }}
              onChange={(e) => handleInputChange(e.target.value)}
            />
          </Row>
          <br/>
          <Row onScroll={onScroll} ref={listInnerRef} gutter={[0, 16]} style={{ height: "40vh", overflow: "scroll"}}>
            
            <Col span={24} style={{ padding: "0px" }}>
              {(contactsFetched?.length > 0) && contactsFetched.map((contact) => {
                return (
                  <Row key={contact.contactId} style={{ margin: "5px 0px" }}>
                    <Col span={24} style={{ padding: "0px" }}>
                      <Checkbox
                        value={contact.contactId}
                        onChange={(event) => onContactChecked(event, contact)}
                        checked={selectedContacts?.includes(contact.contactId)}
                      >
                        {contact.fullName}
                      </Checkbox>
                    </Col>
                  </Row>
                );
              })}
              <br />
              <div name="no-more-contacts-found" style={hasMoreElementsToFetch() ? { display: 'none' } : { display: 'block' }}>
                No more contacts to view {getSearchQueryInput()?.trim()?.length > 3 ? "for search '" + getSearchQueryInput() + "'" : ""}
              </div>
              <div name="continue-typing" style={(getSearchQueryInput()?.trim()?.length > 0 && getSearchQueryInput()?.trim()?.length < 3) ? { display: 'block' } : { display: 'none' }}>
                Continue typing to search...
              </div>
              <div name="contact-search-loader" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '10vh',
                width: '100%'
              }}>
                <Skeleton.Avatar
                  style={searchIsLoading ? { display: 'block' } : { display: 'none' }}
                  active
                  size="default"
                  shape="circle"
                />
              </div>
            </Col>
            
          </Row>
        </Modal>
      )}
    </>
  );
};

export default ContactSelector;